import type { ActivityGroupCategory, ActivityLogEntry } from './types';

type ActivityCategoryPresentation = {
    iconName: string;
    label: string;
    pluralLabel: string;
};

const SHOW_STRUCTURE_TYPES = new Set<ActivityLogEntry['entityType']>(['act', 'scene', 'character']);

const CATEGORY_PRESENTATION: Record<ActivityGroupCategory, ActivityCategoryPresentation> = {
    prop: {
        iconName: 'description',
        label: 'prop',
        pluralLabel: 'props',
    },
    costume: {
        iconName: 'checkroom',
        label: 'costume piece',
        pluralLabel: 'costume pieces',
    },
    blocking: {
        iconName: 'map',
        label: 'blocking path',
        pluralLabel: 'blocking paths',
    },
    schedule: {
        iconName: 'calendar_today',
        label: 'schedule event',
        pluralLabel: 'schedule events',
    },
    act: {
        iconName: 'theater_comedy',
        label: 'act',
        pluralLabel: 'acts',
    },
    scene: {
        iconName: 'theater_comedy',
        label: 'scene',
        pluralLabel: 'scenes',
    },
    character: {
        iconName: 'group',
        label: 'character',
        pluralLabel: 'characters',
    },
    show_structure: {
        iconName: 'theater_comedy',
        label: 'show structure item',
        pluralLabel: 'show structure items',
    },
    project_write: {
        iconName: 'edit_note',
        label: 'project update',
        pluralLabel: 'project updates',
    },
    mixed: {
        iconName: 'history',
        label: 'production change',
        pluralLabel: 'production changes',
    },
};

const ACTION_VERBS: Record<ActivityLogEntry['actionType'], string> = {
    created: 'created',
    deleted: 'deleted',
    updated: 'updated',
};

const PROJECT_WRITE_FEATURE_LABELS: Record<string, string> = {
    admin_adjacent: 'feed and admin',
    attendance: 'attendance',
    blocking_choreography: 'blocking and choreography',
    department_tracking: 'department tracking',
    files: 'files',
    notes_reports: 'notes and reports',
    notifications_email: 'notifications and email',
    people_casting: 'people and casting',
    performance: 'performance',
    schedule: 'schedule',
    script_show: 'script and show structure',
    setup_access: 'setup and access',
};

const PROJECT_WRITE_KIND_LABELS: Record<string, string> = {
    deleteArtifact: 'file or media deletion',
    deleteEntity: 'item deletion',
    orderedListCommand: 'order update',
    patchEntity: 'item update',
    replaceArtifact: 'file or media update',
    reviewOnly: 'review item',
    setField: 'setting update',
    sideEffectCommand: 'command',
};

export function getActivityGroupCategory(entry: ActivityLogEntry): ActivityGroupCategory {
    return SHOW_STRUCTURE_TYPES.has(entry.entityType) ? 'show_structure' : entry.entityType;
}

export function getActivityGroupCategoryForEntries(entries: ActivityLogEntry[]): ActivityGroupCategory {
    const categories = new Set(entries.map(getActivityGroupCategory));
    if (categories.size === 1) {
        return categories.values().next().value ?? 'mixed';
    }
    return 'mixed';
}

export function getActivityCategoryLabel(category: ActivityGroupCategory, count: number): string {
    const presentation = CATEGORY_PRESENTATION[category] ?? CATEGORY_PRESENTATION.mixed;
    return count === 1 ? presentation.label : presentation.pluralLabel;
}

export function getActivityIconName(category: ActivityGroupCategory): string {
    return (CATEGORY_PRESENTATION[category] ?? CATEGORY_PRESENTATION.mixed).iconName;
}

export function getActivityGroupSummary(entries: ActivityLogEntry[]): string {
    const firstEntry = entries[0];
    if (!firstEntry) {
        return '';
    }

    const category = getActivityGroupCategoryForEntries(entries);
    const verb = category === 'project_write' ? 'applied' : ACTION_VERBS[firstEntry.actionType];
    const label = getActivityCategoryLabel(category, entries.length);
    return `${firstEntry.userName} ${verb} ${entries.length} ${label}`;
}

function humanizeToken(value: string): string {
    return value.replace(/_/g, ' ');
}

function getProjectWriteSummary(entry: ActivityLogEntry): string | null {
    if (entry.entityType !== 'project_write' || !entry.feature || !entry.writeKind) {
        return null;
    }

    const featureLabel = PROJECT_WRITE_FEATURE_LABELS[entry.feature] ?? humanizeToken(entry.feature);
    const kindLabel = PROJECT_WRITE_KIND_LABELS[entry.writeKind] ?? humanizeToken(entry.writeKind);
    const reviewSuffix = entry.resolution === 'apply_proposed' ? ' from conflict review' : '';
    return `Applied ${featureLabel} ${kindLabel}${reviewSuffix}`;
}

export function getActivityEntrySummary(entry: ActivityLogEntry): string {
    return getProjectWriteSummary(entry) ?? entry.summary;
}

export function getActivityDetailSummary(entry: ActivityLogEntry): string {
    const summary = getActivityEntrySummary(entry).trim();
    const actorPrefix = `${entry.userName} `;
    if (summary.startsWith(actorPrefix)) {
        return summary.slice(actorPrefix.length);
    }
    return summary;
}
